import { useEffect, useState } from 'react';

import { Btn, SkeletonText, useToast } from '@/components/ui';
import { createRole, deleteRole, fetchRoleMaps, updateRole, updateRoleMap } from '@/api/admin';
import { getErrorMessage } from '@/api';
import { MENU_KEYS, MENU_LABELS } from '@/api/types';
import type { MenuKey, RoleDefinition, RoleMenuLevel } from '@/api/types';

import { ADMIN_OPTION } from './roleOptions';
import styles from '../Admin.module.css';

// [역할별 권한] 탭 — 고정(Admin·Guest) + 기본 제공(Leader·Member) + 커스텀 역할.
// 역할마다 '접근 메뉴 설정' 모달로 메뉴 레벨을 지정하고, 커스텀 역할은 만들고·고치고·지울 수 있다.
// 사용자가 역할을 여러 개 가지면 메뉴마다 가장 높은 레벨이 적용된다(서버 deps.resolve_menu_level).

const LEVEL_ORDER: { key: RoleMenuLevel; label: string }[] = [
  { key: 'none', label: '접근 불가' },
  { key: 'access', label: '접근' },
  { key: 'edit_request', label: '편집 요청' },
  { key: 'edit_direct', label: '편집' },
];

// 매핑 행이 없을 때의 기본값 — backend deps.default_menu_level 과 동일해야 한다. 커스텀 역할은 없음.
const DEFAULT_OPEN_MENUS: MenuKey[] = ['projects', 'requests', 'members', 'catalog', 'bq_assets', 'api_tokens', 'api_guide'];

function defaultLevel(role: string, menu: MenuKey): RoleMenuLevel {
  if (role !== 'leader' && role !== 'member') return 'none';
  if (menu === 'network_meta') return 'edit_request';
  return DEFAULT_OPEN_MENUS.includes(menu) ? 'access' : 'none';
}

type RoleLevels = Partial<Record<MenuKey, RoleMenuLevel>>;

interface EditorState {
  mode: 'create' | 'edit';
  key: string;
  label: string;
  description: string;
}

interface RolesTabProps {
  roles: RoleDefinition[] | null;
  onRolesChanged: () => void;
}

export function RolesTab({ roles, onRolesChanged }: RolesTabProps) {
  const toast = useToast();
  const [roleMaps, setRoleMaps] = useState<Record<string, RoleLevels>>({});
  const [busy, setBusy] = useState(false);
  const [menuModal, setMenuModal] = useState<{ role: RoleDefinition; draft: RoleLevels } | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<RoleDefinition | null>(null);

  const loadRoleMaps = () =>
    fetchRoleMaps()
      .then((res) => setRoleMaps(res.roles))
      .catch((err) => toast.error(getErrorMessage(err)));

  useEffect(() => {
    void loadRoleMaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveLevel = (role: string, menu: MenuKey): RoleMenuLevel =>
    roleMaps[role]?.[menu] ?? defaultLevel(role, menu);

  const openMenuModal = (role: RoleDefinition) => {
    const draft: RoleLevels = {};
    for (const menu of MENU_KEYS) draft[menu] = effectiveLevel(role.key, menu);
    setMenuModal({ role, draft });
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveMenuModal = () =>
    run(async () => {
      if (!menuModal) return;
      // 전 메뉴를 명시 값으로 저장 — 기본값 의존을 없애 화면과 서버 상태를 일치시킨다.
      const levels: Record<string, RoleMenuLevel | null> = {};
      for (const menu of MENU_KEYS) levels[menu] = menuModal.draft[menu] ?? 'none';
      const res = await updateRoleMap(menuModal.role.key, levels);
      setRoleMaps(res.roles);
      toast.success(`${menuModal.role.label} 역할의 접근 메뉴를 저장했어요`);
      setMenuModal(null);
    });

  const saveEditor = () =>
    run(async () => {
      if (!editor) return;
      const label = editor.label.trim();
      const description = editor.description.trim() || null;
      if (editor.mode === 'create') {
        const created = await createRole({ key: editor.key.trim(), label, description });
        toast.success(`${created.label} 역할을 만들었어요 — 접근 메뉴를 설정해 주세요`);
        setEditor(null);
        onRolesChanged();
        openMenuModal(created);
      } else {
        await updateRole(editor.key, { label, description });
        toast.success(`${label} 역할을 수정했어요`);
        setEditor(null);
        onRolesChanged();
      }
    });

  const confirmDelete = () =>
    run(async () => {
      if (!deleting) return;
      const res = await deleteRole(deleting.key);
      toast.success(
        res.unassigned_users
          ? `${deleting.label} 역할을 삭제했어요 — ${res.unassigned_users}명에게서 해제됨`
          : `${deleting.label} 역할을 삭제했어요`,
      );
      setDeleting(null);
      onRolesChanged();
      void loadRoleMaps();
    });

  return (
    <div className={styles.roleMapSection}>
      <div className={styles.roleTabHead}>
        <div className={styles.menuMatrixNote}>
          역할마다 접근 가능한 메뉴와 레벨(접근 불가 / 접근 / 편집 요청 / 편집)을 지정합니다.
          사용자에게 역할을 여러 개 주면 메뉴마다 가장 높은 레벨이 적용돼요.
        </div>
        <Btn
          variant="primary"
          onClick={() => setEditor({ mode: 'create', key: '', label: '', description: '' })}
          disabled={busy}
        >
          새 역할 만들기
        </Btn>
      </div>

      <div className={styles.table}>
        <div className={styles.roleListHead}>
          <span>역할</span>
          <span>접근 메뉴</span>
          <span style={{ textAlign: 'right' }}>설정</span>
        </div>
        <FixedRoleRow label={ADMIN_OPTION.label} desc={ADMIN_OPTION.description ?? ''} summary="전체 메뉴 편집" />
        {roles === null && (
          <div style={{ padding: 16 }}>
            <SkeletonText lines={3} gap={14} />
          </div>
        )}
        {(roles ?? []).map((role) => {
          const visible = MENU_KEYS.filter((m) => effectiveLevel(role.key, m) !== 'none');
          return (
            <div key={role.key} className={styles.roleListRow}>
              <div>
                <div className={styles.roleTitleLine}>
                  <span className={styles.roleId}>{role.label}</span>
                  <code className={styles.roleKey}>{role.key}</code>
                  {!role.is_system && <span className={styles.roleCustomTag}>커스텀</span>}
                </div>
                <div className={styles.roleDesc}>
                  {role.description || '설명 없음'} · {role.user_count}명
                </div>
              </div>
              <div className={styles.roleMenuSummary}>
                {visible.length === 0 ? (
                  <span className={styles.roleMenuNone}>접근 가능한 메뉴 없음</span>
                ) : (
                  visible.map((m) => (
                    <span key={m} className={styles.roleMenuChip}>
                      {MENU_LABELS[m]}
                      <b>{LEVEL_ORDER.find((l) => l.key === effectiveLevel(role.key, m))?.label}</b>
                    </span>
                  ))
                )}
              </div>
              <div className={styles.roleRowActions}>
                <Btn variant="default" onClick={() => openMenuModal(role)} disabled={busy}>
                  접근 메뉴
                </Btn>
                <Btn
                  variant="default"
                  onClick={() =>
                    setEditor({ mode: 'edit', key: role.key, label: role.label, description: role.description ?? '' })
                  }
                  disabled={busy}
                >
                  수정
                </Btn>
                {!role.is_system && (
                  <Btn variant="danger" onClick={() => setDeleting(role)} disabled={busy}>
                    삭제
                  </Btn>
                )}
              </div>
            </div>
          );
        })}
        <FixedRoleRow label="Guest" desc="역할이 하나도 없는 사용자 — 아무 메뉴에도 접근할 수 없음" summary="고정 (역할 부여로만 개방)" muted />
      </div>

      {menuModal && (
        // 편집 중인 모달은 바깥 클릭으로 닫지 않는다 — 잘못 클릭해 입력이 날아가지 않게(취소 버튼으로만 닫힘).
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} style={{ width: 'min(560px, calc(100vw - 32px))' }}>
            <div className={styles.modalTitle}>
              접근 메뉴 설정 — <span className={styles.roleId}>{menuModal.role.label}</span>
            </div>
            <div className={styles.modalBody}>이 역할의 사용자가 각 메뉴에서 무엇을 할 수 있는지 선택하세요.</div>
            <div className={styles.menuMatrix} style={{ margin: '0 0 16px' }}>
              {MENU_KEYS.map((menu) => {
                const current = menuModal.draft[menu] ?? 'none';
                return (
                  <div key={menu} className={styles.menuRow}>
                    <span className={styles.menuName}>{MENU_LABELS[menu]}</span>
                    <div className={styles.menuLevels}>
                      {LEVEL_ORDER.map((lv) => (
                        <button
                          key={lv.key}
                          type="button"
                          className={`${styles.levelBtn} ${current === lv.key ? styles.levelOn : ''}`}
                          onClick={() =>
                            setMenuModal((prev) => (prev ? { ...prev, draft: { ...prev.draft, [menu]: lv.key } } : prev))
                          }
                        >
                          {lv.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.modalActions}>
              <Btn variant="default" onClick={() => setMenuModal(null)}>
                취소
              </Btn>
              <Btn variant="primary" onClick={() => void saveMenuModal()} disabled={busy}>
                저장
              </Btn>
            </div>
          </div>
        </div>
      )}

      {editor && (
        <div className={styles.modalBackdrop}>
          <form
            className={styles.modal}
            onSubmit={(e) => {
              e.preventDefault();
              void saveEditor();
            }}
          >
            <div className={styles.modalTitle}>{editor.mode === 'create' ? '새 역할 만들기' : '역할 수정'}</div>
            <label className={styles.formField}>
              <span className={styles.formLabel}>역할 키</span>
              <input
                className={styles.formInput}
                value={editor.key}
                onChange={(e) => setEditor({ ...editor, key: e.target.value })}
                placeholder="예: data_analyst"
                disabled={editor.mode === 'edit'}
                required
                pattern="[a-z][a-z0-9_]{1,31}"
                title="영문 소문자로 시작하는 2~32자(소문자·숫자·_)"
                autoFocus={editor.mode === 'create'}
              />
              <span className={styles.formHint}>
                {editor.mode === 'create' ? '영문 소문자·숫자·_ 2~32자. 만든 뒤에는 바꿀 수 없어요.' : '역할 키는 바꿀 수 없어요.'}
              </span>
            </label>
            <label className={styles.formField}>
              <span className={styles.formLabel}>표시 이름</span>
              <input
                className={styles.formInput}
                value={editor.label}
                onChange={(e) => setEditor({ ...editor, label: e.target.value })}
                placeholder="예: 데이터 분석가"
                maxLength={40}
                required
                autoFocus={editor.mode === 'edit'}
              />
            </label>
            <label className={styles.formField}>
              <span className={styles.formLabel}>설명 (선택)</span>
              <input
                className={styles.formInput}
                value={editor.description}
                onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                placeholder="이 역할이 하는 일"
                maxLength={200}
              />
            </label>
            <div className={styles.modalActions}>
              <Btn variant="default" onClick={() => setEditor(null)}>
                취소
              </Btn>
              <Btn variant="primary" type="submit" disabled={busy}>
                {editor.mode === 'create' ? '만들기' : '저장'}
              </Btn>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <div className={styles.modalBackdrop} onClick={() => setDeleting(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTitle}>역할 삭제</div>
            <div className={styles.modalBody}>
              <b>{deleting.label}</b> 역할과 접근 메뉴 설정을 삭제합니다.
              {deleting.user_count > 0 && (
                <>
                  <br />이 역할을 가진 <b>{deleting.user_count}명</b>에게서 역할이 해제되고, 다른 역할이 없으면 Guest가 돼요.
                </>
              )}
            </div>
            <div className={styles.modalActions}>
              <Btn variant="default" onClick={() => setDeleting(null)}>
                취소
              </Btn>
              <Btn variant="danger" onClick={() => void confirmDelete()} disabled={busy}>
                삭제
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FixedRoleRow({ label, desc, summary, muted = false }: { label: string; desc: string; summary: string; muted?: boolean }) {
  return (
    <div className={styles.roleListRow}>
      <div>
        <div className={styles.roleTitleLine}>
          <span className={`${styles.roleId} ${muted ? styles.roleIdMuted : ''}`}>{label}</span>
          <span className={styles.roleFixedTag}>고정</span>
        </div>
        <div className={styles.roleDesc}>{desc}</div>
      </div>
      <div className={styles.roleMenuSummary}>
        <span className={muted ? styles.roleMenuNone : styles.roleMenuChip}>{summary}</span>
      </div>
      <div className={styles.roleRowActions} />
    </div>
  );
}
